import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { forwardRef, useMemo, useState } from "react";
import { sendEventEmail } from "../../services/firestore/fireSideChatService";

const SixthSection = forwardRef(({ onExploreEvents }, ref) => {
  const categories = useMemo(
    () => ["Workshops", "Webinars", "Networking", "Mentorship", "Partnerships"],
    []
  );

  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [isError, setIsError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const email = form.email.value;
    const category = form.category.value;

    setLoading(true);
    setFeedback("");
    setIsError(false);

    try {
      // Call your backend API with type "interest"
      await sendEventEmail({
        type: category === "Partnerships" ? "partnership" : "interest",
        email,
        title: category
          ? category === "Partnerships"
            ? "Partnership Enquiry"
            : `${category} Events`
          : "NextLoop Events",
        name: ""
      });

      setFeedback("✅ Thank you! We'll notify you about upcoming events.");
      setIsError(false);
      form.reset();
    } catch (err) {
      console.error(err);
      setFeedback("❌ Something went wrong. Please try again.");
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      ref={ref}
      className="relative w-full flex flex-col items-center text-center py-24 px-6 bg-[#0f0f10] overflow-hidden"
    >
      {/* Soft gradient highlight */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(50,98,144,0.55),transparent_70%)]" />

      {/* Decorative lines background */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <svg className="w-full h-full" viewBox="0 0 500 500" preserveAspectRatio="none">
          <path d="M0 50 Q250 150 500 50" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="2" />
          <path d="M0 200 Q250 300 500 200" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="2" />
          <path d="M0 350 Q250 450 500 350" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="2" />
        </svg>
      </div>

      {/* Content */}
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative text-white font-serif text-5xl md:text-6xl font-semibold max-w-4xl leading-tight"
      >
        Join Our <br />
        Mentorship, <span className="text-[#92abc4] inline-block align-middle">Networking</span> & Partnerships
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="relative mt-6 text-gray-300 text-lg max-w-xl"
      >
        Sign up to receive event updates, early access, or express interest in partnering with us on future initiatives.
      </motion.p>

      {/* Categories */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="relative flex flex-wrap justify-center items-center gap-4 mt-10 text-gray-300"
      >
        {categories.map((c, idx) => (
          <span key={idx} className="flex items-center gap-4 text-base">
            {c}
            {idx !== categories.length - 1 && <span className="text-gray-600">—</span>}
          </span>
        ))}
      </motion.div>

      {/* Email + Category Form */}
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.25 }}
        className="relative mt-12 flex flex-col md:flex-row items-center gap-4"
      >
        <input
          type="email"
          name="email"
          required
          placeholder="Enter your email"
          disabled={loading}
          className="px-5 py-3 rounded-full bg-white/10 backdrop-blur text-white placeholder-gray-400 border border-white/20 focus:border-green-400 outline-none w-full md:w-72 disabled:opacity-50"
        />

        <select
          name="category"
          required
          disabled={loading}
          className="px-5 py-3 rounded-full bg-white/10 backdrop-blur text-white border border-white/20 focus:border-green-400 outline-none w-full md:w-60 disabled:opacity-50"
        >
          <option value="" disabled selected>
            Select event type
          </option>
          {categories.map((c, idx) => (
            <option key={idx} value={c} className="text-black">
              {c}
            </option>
          ))}
        </select>

        <button
          type="submit"
          disabled={loading}
          className="w-full md:w-auto bg-[#92abc4] text-white px-8 py-3 rounded-full flex items-center justify-center gap-2 font-medium hover:bg-green-400 transition shadow-lg disabled:opacity-50"
        >
          {loading ? "Submitting..." : "SUBMIT"} <ArrowRight size={20} />
        </button>
      </motion.form>

      <p className="mt-4 text-sm text-gray-400 max-w-md">
        Interested in sponsoring, co-hosting, or collaborating with Nextloop Gateway?
        Select <span className="text-[#92abc4] font-medium">Partnerships</span> above.
      </p>
      {/* Feedback message */}
      {feedback && (
        <p className={`mt-4 ${isError ? "text-red-400" : "text-green-400"}`}>
          {feedback}
        </p>
      )}

      {/* CTA Button */}
      {/* <motion.button
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, delay: 0.25 }}
        onClick={onExploreEvents}
        className="relative mt-12 bg-green-500 text-white px-8 py-4 rounded-full flex items-center gap-2 font-medium hover:bg-green-400 transition shadow-lg"
      >
        EXPLORE EVENTS <ArrowRight size={20} />
      </motion.button>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="relative mt-10 flex items-center gap-4"
      >
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="w-12 h-12 rounded-full overflow-hidden border-2 border-green-400">
            <img
              src={`https://i.pravatar.cc/150?img=${i + 10}`}
              alt="mentor"
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </motion.div> */}
    </section>
  );
});

export default SixthSection;
